import React, { useState } from 'react';
import { 
    ChevronRight, MapPin, Clock, PoundSterling,
    Brain, Zap, GraduationCap, Flag, Users, MessageCircle,
    RefreshCw, MessageSquare, TrendingUp, Star
} from 'lucide-react';
import { Button } from './ui/button';
import FoundingBadge from './FoundingBadge';

// Category icon mapping
const CATEGORY_ICONS = {
    execution: Zap,
    problem_solving: Brain,
    ownership: Flag,
    adaptability: RefreshCw,
    communication: MessageSquare,
    collaboration: Users,
    commercial: TrendingUp
};

/**
 * ShortviewTalentCard - Employer-facing candidate card
 * Displays key metrics for quick scanning and comparison
 */
const ShortviewTalentCard = ({ 
    candidate, 
    onExpand, 
    onShortlist, 
    onCompare,
    onBookTrial,
    isSelected = false,
    isWildcard = false,
    alignmentScore = null
}) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [showTrialPerf, setShowTrialPerf] = useState(false);
    const { talent_card, name, id } = candidate;
    
    // Extract data from talent card
    const header = talent_card?.header || {};
    const snapshotScore = talent_card?.snapshot_score || {};
    const strengthTags = talent_card?.strength_tags || {};
    const trialReadiness = talent_card?.trial_readiness || {};
    const wildcardData = talent_card?.wildcard || {};
    const titleTag = talent_card?.title_tag || {};
    const trialFeedback = talent_card?.trial_feedback || null;
    
    // Get dimension scores — handle both formats (dimensions, skill_dimensions, snapshot_scores)
    const dimensions = snapshotScore.dimensions || snapshotScore.skill_dimensions || {};
    const flatScores = snapshotScore.snapshot_scores || {};
    const overallScore = snapshotScore.overall_score || snapshotScore.composite_score || snapshotScore.calculated_snapshot_score || 0;
    
    // Signal icons mapping
    const signalIcons = {
        problem_solving: Brain,
        execution_delivery: Zap,
        learning_speed: GraduationCap,
        ownership_initiative: Flag,
        collaboration: Users,
        communication: MessageCircle
    };
    
    // Signal display names
    const signalNames = {
        problem_solving: 'Problem Solving',
        execution_delivery: 'Execution & Delivery',
        learning_speed: 'Learning Speed',
        ownership_initiative: 'Ownership & Initiative',
        collaboration: 'Collaboration',
        communication: 'Communication'
    };
    
    // Role alignment score — backend returns `role_alignment_score`; guard
    // against undefined/null/NaN with typeof so Math.round never sees non-numbers.
    const roleAlignment = alignmentScore
        ? (alignmentScore.role_alignment_score ?? alignmentScore.overall_alignment_score ?? null)
        : null;
    
    // Get trial readiness summary
    const trialShortView = trialReadiness.short_view || {};
    const availability = trialShortView.timeline || trialReadiness.summary?.split('.')[0] || 'Flexible';
    const location = trialShortView.location_flexibility || header.location || 'London';
    const payFit = trialReadiness.expanded_view?.compensation_expectation || 'Market rate';
    
    // Check if this is a true wildcard
    const showWildcardStyle = isWildcard || wildcardData.is_wildcard;
    
    return (
        <div 
            className={`
                relative rounded-2xl border-2 transition-all duration-300 cursor-pointer overflow-hidden
                ${isSelected ? 'border-purple-500 shadow-lg shadow-purple-100' : 
                  showWildcardStyle ? 'border-slate-400/50' : 'border-gray-200 hover:border-purple-300 bg-white'}
            `}
            style={{ 
                width: '100%',
                maxWidth: '320px',
                minHeight: '480px',
                ...(showWildcardStyle ? {
                    background: 'linear-gradient(165deg, #f8fafc 0%, #e2e8f0 15%, #cbd5e1 35%, #94a3b8 50%, #cbd5e1 65%, #e2e8f0 85%, #f8fafc 100%)',
                    boxShadow: '0 8px 32px rgba(100, 116, 139, 0.35), 0 4px 16px rgba(148, 163, 184, 0.25), inset 0 2px 4px rgba(255, 255, 255, 0.6), inset 0 -1px 2px rgba(100, 116, 139, 0.2)'
                } : {})
            }}
            data-testid={`shortview-card-${id}`}
        >
            {/* Wildcard Silver Glossy Card Effect - Full card treatment */}
            {showWildcardStyle && (
                <>
                    {/* Animated shimmer sweep across entire card */}
                    <div 
                        className="absolute inset-0 rounded-2xl pointer-events-none z-10"
                        style={{
                            background: 'linear-gradient(105deg, transparent 20%, rgba(255, 255, 255, 0) 40%, rgba(255, 255, 255, 0.9) 50%, rgba(255, 255, 255, 0) 60%, transparent 80%)',
                            backgroundSize: '250% 100%',
                            animation: 'cardShimmer 4s ease-in-out infinite'
                        }}
                    />
                    
                    {/* Glossy top reflection */}
                    <div 
                        className="absolute top-0 left-0 right-0 h-32 pointer-events-none z-0"
                        style={{
                            background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0.2) 40%, transparent 100%)',
                            borderRadius: '16px 16px 0 0'
                        }}
                    />
                    
                    {/* Subtle inner glow */}
                    <div 
                        className="absolute inset-0 rounded-2xl pointer-events-none z-0"
                        style={{
                            boxShadow: 'inset 0 0 40px rgba(255, 255, 255, 0.3), inset 0 0 80px rgba(148, 163, 184, 0.1)'
                        }}
                    />
                    
                    {/* Metallic edge highlight */}
                    <div 
                        className="absolute inset-0 rounded-2xl pointer-events-none z-0"
                        style={{
                            border: '1px solid rgba(255, 255, 255, 0.4)',
                            borderBottom: '1px solid rgba(100, 116, 139, 0.3)'
                        }}
                    />
                </>
            )}
            
            <div className="p-5 flex flex-col h-full relative z-1">
                {/* Header: Name + Scores */}
                <div className="mb-4">
                    <span className="inline-flex items-center gap-2 mb-2">
                        <h3 
                            className={`text-xl font-bold ${showWildcardStyle ? 'text-slate-800' : 'text-gray-900'}`}
                            style={{ fontFamily: 'Unbounded, sans-serif' }}
                        >
                            {(name || header.name || 'Candidate').split(' ')[0]}
                        </h3>
                        {header?.founding_member_number != null && (
                            <FoundingBadge number={header.founding_member_number} />
                        )}
                    </span>
                    {(() => {
                        const cr = header?.current_role;
                        if (!cr || typeof cr !== 'object') return null;
                        let line = null;
                        if (cr.status === 'Looking for my first role') {
                            line = 'Seeking first role';
                        } else if (cr.title) {
                            const base = cr.company ? `${cr.title} at ${cr.company}` : cr.title;
                            line = cr.years ? `${base} · ${cr.years}` : base;
                        }
                        if (!line) return null;
                        return (
                            <p className="text-sm text-slate-500 -mt-1 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid={`shortview-role-line-${id}`}>
                                {line}
                            </p>
                        );
                    })()}
                    <div className="flex items-center justify-between">
                        {typeof roleAlignment === 'number' && (
                            <div className="flex items-center space-x-1" data-testid={`alignment-score-${id}`}>
                                <span className={`text-sm ${showWildcardStyle ? 'text-slate-600' : 'text-gray-500'}`}>Alignment</span>
                                <span className="text-lg font-bold" style={{ color: showWildcardStyle ? '#475569' : '#9333EA' }}>
                                    {Math.round(roleAlignment)}%
                                </span>
                            </div>
                        )}
                        <div className="flex items-center space-x-1">
                            <span className={`text-sm ${showWildcardStyle ? 'text-slate-600' : 'text-gray-500'}`}>Score</span>
                            <span className="text-lg font-bold" style={{ color: '#14B8A6' }}>
                                {typeof overallScore === 'number' && overallScore > 0 ? overallScore.toFixed(1) : '–'}
                            </span>
                            {typeof overallScore === 'number' && overallScore > 0 && (
                                <span className={`text-sm ${showWildcardStyle ? 'text-slate-500' : 'text-gray-400'}`}>/5</span>
                            )}
                        </div>
                    </div>
                </div>
                
                {/* Divider */}
                <div className={`border-t mb-4 ${showWildcardStyle ? 'border-slate-200' : 'border-gray-100'}`} />
                
                {/* Title Tag - Category Icon + Title with Hover Tooltip */}
                <div className="mb-4 relative">
                    <div 
                        className="relative overflow-hidden rounded-xl px-3 py-2.5 cursor-pointer"
                        style={{
                            background: showWildcardStyle 
                                ? 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(241,245,249,0.9) 50%, rgba(226,232,240,0.8) 100%)'
                                : 'linear-gradient(135deg, #ffffff 0%, #f3e8ff 50%, #ede9fe 100%)',
                            border: showWildcardStyle ? '2px solid #94a3b8' : '2px solid #a855f7',
                            boxShadow: showWildcardStyle 
                                ? '0 3px 12px rgba(100, 116, 139, 0.2), inset 0 1px 0 rgba(255,255,255,0.8)'
                                : '0 3px 12px rgba(168, 85, 247, 0.25), inset 0 1px 0 rgba(255,255,255,0.8)'
                        }}
                        onMouseEnter={() => setShowTooltip(true)}
                        onMouseLeave={() => setShowTooltip(false)}
                        onClick={() => setShowTooltip(!showTooltip)}
                    >
                        <div className="flex items-center space-x-2.5">
                            {/* Category Icon */}
                            {(() => {
                                const category = titleTag.category || strengthTags.top_tag?.category || 'execution';
                                const IconComponent = CATEGORY_ICONS[category] || Zap;
                                return (
                                    <div 
                                        className="w-9 h-9 rounded-lg flex items-center justify-center"
                                        style={{
                                            background: showWildcardStyle
                                                ? 'linear-gradient(135deg, #64748b 0%, #94a3b8 100%)'
                                                : 'linear-gradient(135deg, #9333ea 0%, #a855f7 100%)',
                                            boxShadow: showWildcardStyle
                                                ? '0 2px 6px rgba(100, 116, 139, 0.4)'
                                                : '0 2px 6px rgba(147, 51, 234, 0.4)'
                                        }}
                                    >
                                        <IconComponent className="h-5 w-5 text-white" />
                                    </div>
                                );
                            })()}
                            <div>
                                <span 
                                    className="font-bold uppercase tracking-wide text-sm block"
                                    style={{ color: showWildcardStyle ? '#475569' : '#7c3aed' }}
                                >
                                    {titleTag.title || strengthTags.top_tag?.name || '—'}
                                </span>
                            </div>
                        </div>
                    </div>
                    
                    {/* Tooltip */}
                    {showTooltip && (titleTag.tooltip || strengthTags.top_tag?.description) && (
                        <div 
                            className="absolute left-0 right-0 top-full mt-2 p-3 rounded-lg shadow-lg z-50 text-sm"
                            style={{
                                background: 'white',
                                border: '1px solid #e2e8f0',
                                boxShadow: '0 10px 25px rgba(0,0,0,0.15)'
                            }}
                        >
                            <p className="text-gray-700 leading-relaxed">
                                {titleTag.tooltip || strengthTags.top_tag?.description}
                            </p>
                        </div>
                    )}
                </div>
                
                {/* Divider */}
                <div className={`border-t mb-4 ${showWildcardStyle ? 'border-slate-200' : 'border-gray-100'}`} />
                
                {/* Signal Scores */}
                <div className="space-y-2 mb-4 flex-grow">
                    <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#92400E' }}>Top Signals</p>
                    {Object.entries(signalNames).map(([key, label]) => {
                        const Icon = signalIcons[key];
                        const dimData = dimensions[key];
                        const score = dimData?.score || dimData || flatScores[key] || 0;
                        const displayScore = typeof score === 'number' ? score.toFixed(1) : '0.0';
                        const isLowConfidence = dimData?.confidence === 'low';
                        
                        return (
                            <div key={key} className="flex items-center justify-between" data-testid={`dimension-${key}`}>
                                <div className="flex items-center space-x-2">
                                    <Icon className={`h-4 w-4 ${isLowConfidence ? 'opacity-40' : ''}`} style={{ color: '#92400E' }} />
                                    <span className={`text-sm ${isLowConfidence ? 'opacity-40' : ''}`} style={{ color: '#78350F' }}>{label}</span>
                                </div>
                                <div className="flex items-center space-x-1">
                                    {isLowConfidence && (
                                        <span className="text-[10px] text-slate-400 italic" title="Insufficient data to score reliably">Low data</span>
                                    )}
                                    <span className={`text-sm font-semibold ${isLowConfidence ? 'opacity-40' : ''}`} style={{ color: isLowConfidence ? '#94A3B8' : '#14B8A6' }}>
                                        {displayScore}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
                
                {/* Divider */}
                <div className="border-t border-gray-100 mb-4" />
                
                {/* Availability */}
                <div className="mb-4">
                    <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#92400E' }}>Availability</p>
                    <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center space-x-1" style={{ color: '#78350F' }}>
                            <Clock className="h-3 w-3" />
                            <span className="truncate max-w-[80px]">{availability}</span>
                        </div>
                        <div className="flex items-center space-x-1" style={{ color: '#78350F' }}>
                            <MapPin className="h-3 w-3" />
                            <span className="truncate max-w-[80px]">{location.split('(')[0].trim()}</span>
                        </div>
                        <div className="flex items-center space-x-1" style={{ color: '#78350F' }}>
                            <PoundSterling className="h-3 w-3" />
                            <span className="truncate max-w-[60px]">Fit</span>
                        </div>
                    </div>
                </div>
                
                {/* Divider */}
                <div className="border-t border-gray-100 mb-4" />
                
                {/* Compare Button - Large & Prominent - Only show if handler provided */}
                {onCompare && (
                <Button
                    onClick={(e) => { e.stopPropagation(); onCompare?.(candidate); }}
                    className={`
                        w-full py-3 text-base font-bold transition-all
                        ${isSelected 
                            ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                            : 'bg-gray-900 hover:bg-gray-800 text-white'
                        }
                    `}
                    data-testid={`compare-btn-${id}`}
                >
                    {isSelected ? '✓ SELECTED FOR COMPARISON' : 'COMPARE CANDIDATES'}
                </Button>
                )}
                
                {/* Trial Performance Dropdown */}
                {trialFeedback && (
                    <div className="mb-3">
                        <button
                            onClick={(e) => { e.stopPropagation(); setShowTrialPerf(!showTrialPerf); }}
                            className="w-full flex items-center justify-between px-3 py-2 bg-purple-50 rounded-lg text-sm font-medium text-purple-700 hover:bg-purple-100 transition-colors"
                            data-testid={`trial-perf-btn-${id}`}
                        >
                            <span className="flex items-center">
                                <Star className="h-3.5 w-3.5 mr-1.5" />
                                Trial Performance
                            </span>
                            <ChevronRight className={`h-4 w-4 transition-transform ${showTrialPerf ? 'rotate-90' : ''}`} />
                        </button>
                        {showTrialPerf && (
                            <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm space-y-2">
                                {trialFeedback.ai_summary && (
                                    <p className="text-gray-700 text-xs leading-relaxed">{trialFeedback.ai_summary}</p>
                                )}
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-gray-500">Overall</span>
                                    <span className="font-semibold text-purple-700 capitalize">{trialFeedback.overall_impression?.replace(/_/g, ' ')}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-gray-500">Hire Intent</span>
                                    <span className="font-semibold text-teal-700 capitalize">{trialFeedback.hiring_intent?.replace(/_/g, ' ')}</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                
                {/* Divider */}
                <div className="border-t border-gray-100 my-3" />
                
                {/* Action Buttons — show full row for employers, simplified for candidate */}
                {(onExpand && onBookTrial) ? (
                <div className="flex space-x-2">
                    <Button
                        variant="outline"
                        className="flex-1 text-sm"
                        onClick={(e) => { e.stopPropagation(); onExpand?.(candidate); }}
                        data-testid={`expand-btn-${id}`}
                    >
                        Expand <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                    <Button
                        className="flex-1 text-sm bg-gradient-to-r from-[#8B5CF6] to-[#7C3AED] text-white hover:opacity-90"
                        onClick={(e) => { e.stopPropagation(); onBookTrial?.(candidate); }}
                        data-testid={`book-trial-btn-${id}`}
                    >
                        Book Trial
                    </Button>
                </div>
                ) : onExpand ? (
                <Button
                    variant="outline"
                    className="w-full text-sm"
                    onClick={(e) => { e.stopPropagation(); onExpand?.(candidate); }}
                    data-testid={`expand-btn-${id}`}
                >
                    See Full Breakdown <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
                ) : null}
            </div>
        </div>
    );
};

export default ShortviewTalentCard;
